/**
 * Admin: Weekly Cohort Archive
 * ---------------------------------------------------------------------------
 * Lists every past cohort digest with metadata + status (sent, slack post,
 * skipped, recipients count). Click a row to preview the HTML in a sandboxed
 * iframe. Also lets admins pick THIS week and see a live preview even if
 * Sunday hasn't fired yet.
 *
 * Backend:
 *   - GET /api/admin/leads/weekly-cohort/history?limit=12
 *   - GET /api/admin/leads/weekly-cohort/{iso_week}
 *   - GET /api/admin/leads/weekly-cohort/preview
 *   - POST /api/admin/leads/weekly-cohort/send-now
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CalendarBlank, ChatCircleText, ArrowClockwise, PaperPlaneRight,
  CheckCircle, XCircle, Warning, Envelope, GitDiff, ArrowsLeftRight,
} from "@phosphor-icons/react";
import client from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AdminWeeklyCohortsPage() {
  const { user } = useAuth();
  const [history, setHistory] = useState(null);
  const [loadingHist, setLoadingHist] = useState(true);
  const [selected, setSelected] = useState(null);   // iso_week
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  // Wave 9: Compare mode — pick a second iso_week and render side-by-side
  const [compareMode, setCompareMode] = useState(false);
  const [compareTarget, setCompareTarget] = useState(null); // iso_week of the "b" cohort
  const [compareResult, setCompareResult] = useState(null);
  const [loadingCompare, setLoadingCompare] = useState(false);
  // Wave 10: 12-week trend chart at the top of the archive
  const [trend, setTrend] = useState(null);
  const [loadingTrend, setLoadingTrend] = useState(true);

  const loadTrend = async () => {
    setLoadingTrend(true);
    try {
      const { data } = await client.get("/admin/leads/weekly-cohort/trend?weeks=12");
      setTrend(data);
    } catch (e) {
      /* silent */
    } finally {
      setLoadingTrend(false);
    }
  };

  const loadHistory = async () => {
    setLoadingHist(true);
    try {
      const { data } = await client.get("/admin/leads/weekly-cohort/history?limit=26");
      setHistory(data);
      // Pick the first row by default, or "this week preview" if archive is empty
      if (data.sends?.length > 0 && !selected) {
        setSelected(data.sends[0].iso_week);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load archive");
    } finally {
      setLoadingHist(false);
    }
  };

  const loadDetail = async (iso_week) => {
    if (!iso_week) return;
    setLoadingDetail(true);
    try {
      const { data } = await client.get(`/admin/leads/weekly-cohort/${iso_week}`);
      setDetail(data);
    } catch (e) {
      setDetail(null);
      toast.error(e?.response?.data?.detail || "Failed to load this cohort");
    } finally {
      setLoadingDetail(false);
    }
  };

  const previewThisWeek = async () => {
    setLoadingDetail(true);
    try {
      const { data } = await client.get("/admin/leads/weekly-cohort/preview");
      setDetail({
        iso_week: data.report.iso_week,
        sent_at: null,
        recipients: [],
        stats_summary: data.report.this_week,
        slack_posted: false,
        slack_reason: "preview_only",
        report_preview: data.report,
        html: data.html,
        unsent: true,
      });
      setSelected(data.report.iso_week);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally {
      setLoadingDetail(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm("Send this week's cohort digest to admin recipients + Slack now?")) return;
    setSendingNow(true);
    try {
      const { data } = await client.post("/admin/leads/weekly-cohort/send-now");
      const sent = (data.results || []).filter((r) => r.sent).length;
      toast.success(`Cohort digest fired to ${sent} recipient${sent === 1 ? "" : "s"}${data.slack?.posted ? " + Slack" : ""}`);
      loadHistory();
      loadTrend();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSendingNow(false);
    }
  };

  const runCompare = async (weekA, weekB) => {
    if (!weekA || !weekB || weekA === weekB) return;
    setLoadingCompare(true);
    try {
      const { data } = await client.post("/admin/leads/weekly-cohort/compare", {
        iso_week_a: weekA,
        iso_week_b: weekB,
      });
      setCompareResult(data);
    } catch (e) {
      setCompareResult(null);
      toast.error(e?.response?.data?.detail || "Compare failed");
    } finally {
      setLoadingCompare(false);
    }
  };

  const toggleCompareMode = () => {
    const next = !compareMode;
    setCompareMode(next);
    if (!next) {
      setCompareTarget(null);
      setCompareResult(null);
    }
  };

  useEffect(() => {
    if (compareMode && selected && compareTarget) {
      runCompare(selected, compareTarget);
    }
    // eslint-disable-next-line
  }, [compareMode, selected, compareTarget]);

  useEffect(() => { loadHistory(); loadTrend(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (selected) loadDetail(selected); /* eslint-disable-next-line */ }, [selected]);

  if (!user?.is_admin) {
    return <div className="p-8 text-red-400" data-testid="admin-cohorts-forbidden">Admin only.</div>;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="admin-weekly-cohorts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/30 bg-[#FF5A1F]/10 text-[#FF5A1F] px-3 py-1 text-xs font-semibold mb-3">
            <CalendarBlank size={12} weight="fill" /> WEEKLY COHORT ARCHIVE
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-white tracking-tight">
            Past cohort digests
          </h1>
          <p className="text-neutral-400 mt-2 text-sm sm:text-base max-w-2xl">
            Every Sunday 6pm ET recap that fired — email + Slack status, full HTML preview, side-by-side compare. Preview mid-week to sanity-check the numbers before the auto-send lands.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={toggleCompareMode}
            data-testid="cohorts-compare-mode-btn"
            className={`rounded-md px-3 py-2 text-xs font-semibold border transition-colors flex items-center gap-1.5 ${
              compareMode
                ? "bg-blue-500/15 border-blue-500/40 text-blue-300"
                : "bg-white/[0.05] border-white/10 text-neutral-300 hover:border-white/25"
            }`}
          >
            <GitDiff size={12} weight="bold" /> {compareMode ? "Exit compare" : "Compare"}
          </button>
          <button
            onClick={previewThisWeek}
            disabled={loadingDetail}
            data-testid="cohorts-preview-this-week-btn"
            className="rounded-md px-3 py-2 text-xs font-semibold bg-white/[0.05] border border-white/10 hover:border-white/25 text-neutral-300 disabled:opacity-50 flex items-center gap-1.5"
          >
            <ArrowClockwise size={12} weight="bold" /> Preview this week
          </button>
          <button
            onClick={sendNow}
            disabled={sendingNow}
            data-testid="cohorts-send-now-btn"
            className="rounded-md px-4 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 flex items-center gap-1.5"
          >
            {sendingNow ? <ArrowClockwise size={14} className="animate-spin" /> : <PaperPlaneRight size={14} weight="fill" />}
            Send now
          </button>
        </div>
      </div>

      <CohortTrendChart trend={trend} loading={loadingTrend} />

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        {/* Left rail — history list */}
        <div className="card-surface p-3 max-h-[70vh] overflow-y-auto scrollbar-thin" data-testid="cohort-history-rail">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 px-1">
            History ({history?.total ?? 0})
          </div>
          {loadingHist ? (
            <div className="text-xs text-neutral-500 py-4 text-center">Loading…</div>
          ) : history?.sends?.length === 0 ? (
            <div className="text-xs text-neutral-500 py-6 px-2 text-center leading-relaxed" data-testid="cohort-history-empty">
              No cohort digests fired yet. Click <b className="text-white">Preview this week</b> above to see what Sunday will look like.
            </div>
          ) : (
            <div className="space-y-1">
              {history.sends.map((s) => {
                const isSelected = selected === s.iso_week;
                const isTarget = compareTarget === s.iso_week;
                return (
                  <div key={s.iso_week + s.sent_at} className={`rounded-md transition-colors ${
                    isSelected ? "bg-[#FF5A1F]/10 border border-[#FF5A1F]/40"
                      : isTarget ? "bg-blue-500/10 border border-blue-500/40"
                      : "hover:bg-white/[0.03] border border-transparent"
                  }`}>
                    <button
                      onClick={() => setSelected(s.iso_week)}
                      data-testid={`cohort-row-${s.iso_week}`}
                      className="w-full text-left px-3 py-2"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <b className="text-white text-sm">{s.iso_week}</b>
                        <span className="text-[9px] text-neutral-500">
                          {s.sent_at && new Date(s.sent_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap text-[10px]">
                        <span className="text-neutral-500">
                          <Envelope size={9} weight="fill" className="inline mr-0.5" />
                          {(s.recipients?.length ?? 0)} rcpt
                        </span>
                        {s.slack_posted ? (
                          <span className="text-emerald-300" title="Posted to Slack">
                            <ChatCircleText size={9} weight="fill" className="inline mr-0.5" />
                            Slack
                          </span>
                        ) : s.slack_reason ? (
                          <span className="text-neutral-500" title={s.slack_reason}>
                            <ChatCircleText size={9} className="inline mr-0.5" />
                            {s.slack_reason === "no_webhook" ? "no hook" : "no Slack"}
                          </span>
                        ) : null}
                        {s.stats_summary?.sent > 0 && (
                          <span className="text-neutral-500">{s.stats_summary.sent} sent</span>
                        )}
                      </div>
                    </button>
                    {compareMode && !isSelected && (
                      <button
                        onClick={() => setCompareTarget(isTarget ? null : s.iso_week)}
                        data-testid={`cohort-compare-vs-${s.iso_week}`}
                        className={`w-full text-[10px] font-semibold px-3 py-1 border-t transition-colors ${
                          isTarget
                            ? "bg-blue-500/20 border-blue-500/40 text-blue-200"
                            : "border-white/[0.05] text-neutral-500 hover:text-blue-300"
                        }`}
                      >
                        {isTarget ? "✓ Comparing" : "vs"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right pane — detail (or compare) */}
        <div className="card-surface p-4" data-testid="cohort-detail-pane">
          {compareMode ? (
            <CohortCompareView
              result={compareResult}
              loading={loadingCompare}
              anchorWeek={selected}
              targetWeek={compareTarget}
              onExit={toggleCompareMode}
            />
          ) : loadingDetail ? (
            <div className="text-xs text-neutral-500 py-16 text-center">Loading detail…</div>
          ) : !detail ? (
            <div className="text-xs text-neutral-500 py-16 text-center">
              Pick a cohort from the list, or click <b className="text-white">Preview this week</b> to see the current week.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-semibold text-white">{detail.iso_week}</h2>
                    {detail.unsent ? (
                      <span className="text-[9px] uppercase tracking-widest bg-yellow-500/15 text-yellow-300 border border-yellow-500/40 rounded-full px-1.5 py-0.5 font-semibold" data-testid="cohort-status-unsent">
                        PREVIEW · UNSENT
                      </span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 rounded-full px-1.5 py-0.5 font-semibold" data-testid="cohort-status-sent">
                        <CheckCircle size={9} weight="fill" className="inline mr-0.5" />SENT
                      </span>
                    )}
                    {!detail.unsent && (detail.slack_posted ? (
                      <span className="text-[9px] uppercase tracking-widest bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 rounded-full px-1.5 py-0.5 font-semibold" data-testid="cohort-slack-posted">
                        <ChatCircleText size={9} weight="fill" className="inline mr-0.5" />SLACK
                      </span>
                    ) : (
                      <span className="text-[9px] uppercase tracking-widest bg-neutral-700/40 text-neutral-500 border border-white/10 rounded-full px-1.5 py-0.5 font-semibold" title={detail.slack_reason || ""} data-testid="cohort-slack-missing">
                        <XCircle size={9} weight="fill" className="inline mr-0.5" />NO SLACK
                      </span>
                    ))}
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-1">
                    {detail.sent_at ? `Sent ${new Date(detail.sent_at).toLocaleString()}` : "Not yet sent — mid-week preview"}
                    {detail.recipients?.length > 0 && (
                      <> · to {detail.recipients.length} recipient{detail.recipients.length === 1 ? "" : "s"}</>
                    )}
                  </div>
                </div>
              </div>

              {/* Stat strip */}
              {detail.stats_summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  {[
                    { label: "Sent", val: detail.stats_summary.sent ?? 0 },
                    { label: "Opens", val: detail.stats_summary.opens ?? 0, sub: detail.stats_summary.open_rate_pct ? `${detail.stats_summary.open_rate_pct}%` : null },
                    { label: "Replies", val: detail.stats_summary.replies ?? 0, sub: detail.stats_summary.reply_rate_pct ? `${detail.stats_summary.reply_rate_pct}%` : null },
                    { label: "Bounces", val: detail.stats_summary.bounces ?? 0 },
                  ].map((s) => (
                    <div key={s.label} className="rounded-md bg-white/[0.03] border border-white/10 px-3 py-2">
                      <div className="text-[9px] uppercase tracking-widest text-neutral-500">{s.label}</div>
                      <div className="text-lg font-semibold text-white">{s.val}{s.sub && <span className="text-[10px] text-neutral-500 ml-1">{s.sub}</span>}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* HTML preview */}
              {detail.html ? (
                <div className="border border-white/10 rounded-md overflow-hidden bg-white" data-testid="cohort-html-preview">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-600 px-3 py-2 bg-neutral-100 border-b border-neutral-200">
                    Email preview
                  </div>
                  <iframe
                    title="Cohort digest preview"
                    srcDoc={detail.html}
                    sandbox=""
                    className="w-full min-h-[500px] bg-white"
                    data-testid="cohort-html-iframe"
                  />
                </div>
              ) : (
                <div className="rounded-md p-3 bg-yellow-500/[0.05] border border-yellow-500/30 text-yellow-200 text-[12px] leading-relaxed flex items-center gap-2">
                  <Warning size={12} weight="fill" />
                  This cohort was recorded before the HTML archive feature — no preview available.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CohortCompareView — side-by-side compare of two cohorts with %-change
// coloring per metric. Anchor (A) is the selected row on the left rail;
// target (B) is the row admins clicked "vs" on. Delta = (A - B) / B.
// ---------------------------------------------------------------------------
function CohortCompareView({ result, loading, anchorWeek, targetWeek, onExit }) {
  if (!anchorWeek || !targetWeek) {
    return (
      <div className="text-xs text-neutral-500 py-16 text-center leading-relaxed" data-testid="compare-empty-state">
        <div className="text-base text-white font-semibold mb-2">Compare mode</div>
        Pick a cohort on the left to make it the <b className="text-[#FF5A1F]">anchor</b>,
        then click <b className="text-blue-300">"vs"</b> on another cohort to compare.
        <br /><br />
        <button
          onClick={onExit}
          data-testid="compare-exit-empty-btn"
          className="text-neutral-500 hover:text-white underline"
        >
          Exit compare mode
        </button>
      </div>
    );
  }
  if (loading) {
    return (
      <div className="text-xs text-neutral-500 py-16 text-center" data-testid="compare-loading">
        Loading comparison…
      </div>
    );
  }
  if (!result) return null;

  const rows = [
    { key: "sent", label: "Sent" },
    { key: "opens", label: "Opens" },
    { key: "clicks", label: "Clicks" },
    { key: "replies", label: "Replies" },
    { key: "bounces", label: "Bounces", flipColor: true },
    { key: "open_rate_pct", label: "Open rate %", isPct: true },
    { key: "reply_rate_pct", label: "Reply rate %", isPct: true },
  ];

  const deltaChip = (pct, flipColor = false) => {
    if (pct === 0 || pct === null || pct === undefined) {
      return <span className="text-neutral-500 text-[11px]">—</span>;
    }
    const good = flipColor ? pct < 0 : pct > 0;
    const cls = good ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30" : "text-red-300 bg-red-500/10 border-red-500/30";
    return (
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${cls}`}>
        {pct > 0 ? "↑" : "↓"}{Math.abs(pct)}%
      </span>
    );
  };

  return (
    <div data-testid="cohort-compare-view">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <ArrowsLeftRight size={18} className="text-blue-400" weight="fill" />
          <div>
            <div className="text-base font-semibold text-white">
              <span className="text-[#FF5A1F]">{result.a.iso_week}</span>
              <span className="text-neutral-500 mx-2">vs</span>
              <span className="text-blue-300">{result.b.iso_week}</span>
            </div>
            <div className="text-[11px] text-neutral-500 mt-0.5">
              {result.a.unsent ? "Anchor is this week (preview)" :
                (result.a.sent_at ? `Anchor sent ${new Date(result.a.sent_at).toLocaleDateString()}` : "Anchor sent")}
              {" · "}
              {result.b.sent_at ? `Target sent ${new Date(result.b.sent_at).toLocaleDateString()}` : "Target"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              try {
                const resp = await client.get(
                  `/admin/leads/weekly-cohort/compare/export.csv?iso_week_a=${encodeURIComponent(result.a.iso_week)}&iso_week_b=${encodeURIComponent(result.b.iso_week)}`,
                  { responseType: "blob" }
                );
                const blob = new Blob([resp.data], { type: "text/csv" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = `cohort-compare-${result.a.iso_week}-vs-${result.b.iso_week}.csv`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                URL.revokeObjectURL(url);
                toast.success("Compare CSV downloaded");
              } catch (e) {
                toast.error("Export failed");
              }
            }}
            data-testid="compare-export-csv-btn"
            className="text-[11px] px-3 py-1.5 rounded-md border border-blue-500/40 text-blue-300 hover:bg-blue-500/10 hover:border-blue-500/60 flex items-center gap-1.5 font-semibold"
            title="Download the compare table as CSV"
          >
            📥 CSV
          </button>
          <button
            onClick={onExit}
            data-testid="compare-exit-btn"
            className="text-[11px] px-3 py-1.5 rounded-md border border-white/10 text-neutral-400 hover:text-white hover:border-white/25"
          >
            Exit compare
          </button>
        </div>
      </div>

      <div className="rounded-md overflow-hidden border border-white/10">
        <div className="grid grid-cols-[1fr_100px_100px_100px] text-[10px] uppercase tracking-widest text-neutral-500 bg-white/[0.02] px-4 py-2">
          <div>Metric</div>
          <div className="text-right text-[#FF5A1F]">{result.a.iso_week}</div>
          <div className="text-right text-blue-300">{result.b.iso_week}</div>
          <div className="text-right">Δ</div>
        </div>        {rows.map((r, idx) => {
          const aVal = result.a[r.key] ?? 0;
          const bVal = result.b[r.key] ?? 0;
          const pct = result.deltas[`${r.key}_delta_pct`];
          return (
            <div
              key={r.key}
              data-testid={`compare-row-${r.key}`}
              className={`grid grid-cols-[1fr_100px_100px_100px] items-center px-4 py-3 text-sm ${
                idx % 2 === 0 ? "bg-white/[0.01]" : ""
              } ${idx === rows.length - 1 ? "" : "border-b border-white/[0.05]"}`}
            >
              <div className="text-neutral-300 font-semibold">{r.label}</div>
              <div className="text-right font-mono text-white">
                {r.isPct ? `${aVal}%` : aVal}
              </div>
              <div className="text-right font-mono text-neutral-400">
                {r.isPct ? `${bVal}%` : bVal}
              </div>
              <div className="text-right">
                {deltaChip(pct, r.flipColor)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 text-[10px] text-neutral-500 italic">
        Δ = percent change from <b className="text-blue-300">{result.b.iso_week}</b> → <b className="text-[#FF5A1F]">{result.a.iso_week}</b>. Green = improvement (bounces flipped — fewer is better).
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CohortTrendChart — 12-week line chart of Sent (orange) + Replies (blue)
// rendered as pure SVG (no chart library) so the archive page loads fast.
// Hidden when there's <2 weeks of data (nothing to visualize).
// Wave 11: toggle between raw Counts and Rate % (open_rate / reply_rate)
// ---------------------------------------------------------------------------
function CohortTrendChart({ trend, loading }) {
  const [viewMode, setViewMode] = useState("counts");  // "counts" | "rates"

  if (loading) {
    return (
      <div className="card-surface p-4 mb-4" data-testid="trend-chart-loading">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500">Momentum · last 12 weeks</div>
        <div className="text-xs text-neutral-500 py-8 text-center">Loading trend…</div>
      </div>
    );
  }
  if (!trend || (trend.series || []).length < 2) return null;

  const series = trend.series;
  const width = 720;
  const height = 160;
  const padL = 44, padR = 20, padT = 20, padB = 32;
  const chartW = width - padL - padR;
  const chartH = height - padT - padB;

  const isRates = viewMode === "rates";
  const primaryKey = isRates ? "open_rate_pct" : "sent";
  const secondaryKey = isRates ? "reply_rate_pct" : "replies";
  const primaryLabel = isRates ? "Open rate" : "Sent";
  const secondaryLabel = isRates ? "Reply rate" : "Replies";
  const primaryPeak = isRates
    ? Math.max(...series.map((p) => p.open_rate_pct || 0), 0)
    : (trend.peaks?.max_sent || 0);
  const secondaryPeak = isRates
    ? Math.max(...series.map((p) => p.reply_rate_pct || 0), 0)
    : (trend.peaks?.max_replies || 0);

  // Y-axis maxes to whichever is larger. Rates cap at 100%; counts at
  // the actual peak. Never < 4 so tiny numbers still render.
  const maxY = isRates
    ? Math.max(primaryPeak, secondaryPeak, 10)
    : Math.max(primaryPeak, secondaryPeak, 4);

  const xFor = (i) => padL + (i / Math.max(series.length - 1, 1)) * chartW;
  const yFor = (v) => padT + chartH - (v / maxY) * chartH;

  const pathFor = (key) => series.map((pt, i) => {
    const x = xFor(i);
    const y = yFor(pt[key] || 0);
    return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  const primaryPath = pathFor(primaryKey);
  const secondaryPath = pathFor(secondaryKey);

  const fmtPeak = (v) => isRates ? `${v.toFixed(1)}%` : v;
  const fmtTick = (v) => isRates ? `${v.toFixed(0)}%` : Math.round(v);
  const fmtPoint = (v) => isRates ? `${v.toFixed(1)}%` : v;

  return (
    <div className="card-surface p-4 mb-4" data-testid="trend-chart">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
            Momentum · last {series.length} week{series.length === 1 ? "" : "s"}
          </div>
          <div className="text-sm text-white mt-1">
            {primaryLabel}: <b className="text-[#FF5A1F]">{fmtPeak(primaryPeak)}</b> peak
            <span className="mx-2 text-neutral-600">·</span>
            {secondaryLabel}: <b className="text-blue-300">{fmtPeak(secondaryPeak)}</b> peak
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Wave 11: Counts / Rates toggle */}
          <div
            className="inline-flex items-center rounded-full border border-white/10 bg-white/[0.03] p-0.5 text-[10px]"
            data-testid="trend-chart-view-toggle"
          >
            <button
              onClick={() => setViewMode("counts")}
              data-testid="trend-view-counts"
              className={`px-2.5 py-1 rounded-full transition-colors ${
                viewMode === "counts"
                  ? "bg-white/10 text-white"
                  : "text-neutral-500 hover:text-white"
              }`}
            >
              Counts
            </button>
            <button
              onClick={() => setViewMode("rates")}
              data-testid="trend-view-rates"
              className={`px-2.5 py-1 rounded-full transition-colors ${
                viewMode === "rates"
                  ? "bg-white/10 text-white"
                  : "text-neutral-500 hover:text-white"
              }`}
            >
              Rates %
            </button>
          </div>
          <div className="flex items-center gap-4 text-[10px]">
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#FF5A1F] rounded" /> {primaryLabel}</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-blue-400 rounded" /> {secondaryLabel}</span>
          </div>
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto max-h-56" data-testid="trend-chart-svg">
        {/* Y-axis gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map((frac) => (
          <g key={frac}>
            <line
              x1={padL} x2={width - padR}
              y1={padT + chartH * (1 - frac)}
              y2={padT + chartH * (1 - frac)}
              stroke="rgba(255,255,255,0.06)" strokeWidth="1"
            />
            <text
              x={padL - 6} y={padT + chartH * (1 - frac) + 3}
              fontSize="9" fill="#666" textAnchor="end"
            >
              {fmtTick(maxY * frac)}
            </text>
          </g>
        ))}
        {/* Primary line */}
        <path d={primaryPath} fill="none" stroke="#FF5A1F" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
        {series.map((pt, i) => (
          <circle
            key={`prim-${i}`}
            cx={xFor(i)} cy={yFor(pt[primaryKey] || 0)}
            r="3" fill="#FF5A1F"
          >
            <title>{pt.iso_week} · {primaryLabel} {fmtPoint(pt[primaryKey] || 0)}</title>
          </circle>
        ))}
        {/* Secondary line */}
        <path d={secondaryPath} fill="none" stroke="#60A5FA" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" />
        {series.map((pt, i) => (
          <circle
            key={`sec-${i}`}
            cx={xFor(i)} cy={yFor(pt[secondaryKey] || 0)}
            r="3" fill="#60A5FA"
          >
            <title>{pt.iso_week} · {secondaryLabel} {fmtPoint(pt[secondaryKey] || 0)}</title>
          </circle>
        ))}
        {/* X-axis week labels (every other one to avoid crowding) */}
        {series.map((pt, i) => {
          if (i % Math.max(1, Math.floor(series.length / 6)) !== 0 &&
              i !== series.length - 1) return null;
          return (
            <text
              key={`x-${i}`}
              x={xFor(i)} y={height - 8}
              fontSize="9" fill="#888" textAnchor="middle"
            >
              {pt.iso_week.replace(/^\d{4}-/, "")}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
